import React, { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { billingApi } from '../../services/billing'
import { promoApi } from '../../services/promo'
import { Screen } from '../../components/lease/Screen'
import { BrandHeading } from '../../components/lease/BrandHeading'
import { PrimaryButton } from '../../components/lease/PrimaryButton'
import { PAYMENTS_ENABLED } from '../../config'

const inputClass =
  'mt-2 w-full rounded-xl border border-emerald-200 px-4 py-3 text-base focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'
const labelClass = 'block text-sm font-semibold text-slate-700 dark:text-slate-200'

// "Legal Stuff" gate (mockup 5): the user reviews ToS / Disclaimer / Refund
// Policy and accepts before checkout. Acceptance is stamped server-side.
//
// Property-promo access is a two-step flow: request a code by property
// (backend always replies generically, never confirms a match), then redeem
// the emailed code. "Already have a code?" skips straight to redeem for
// tenants who don't need to request one.
function LegalGate() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const plan = params.get('plan') || 'standard'

  const [stage, setStage] = useState('request') // 'request' | 'code'
  const [requestSent, setRequestSent] = useState(false)

  const [email, setEmail] = useState('')
  const [propertyName, setPropertyName] = useState('')
  const [propertyAddress, setPropertyAddress] = useState('')
  const [requestBusy, setRequestBusy] = useState(false)
  const [requestError, setRequestError] = useState(null)

  const [code, setCode] = useState('')
  const [redeemBusy, setRedeemBusy] = useState(false)
  const [redeemError, setRedeemError] = useState(null)

  const [payBusy, setPayBusy] = useState(false)
  const [payError, setPayError] = useState(null)

  const handleRequest = async () => {
    setRequestBusy(true)
    setRequestError(null)
    try {
      await promoApi.request({
        email: email.trim(),
        property_name: propertyName.trim(),
        property_address: propertyAddress.trim(),
      })
      // Endpoint is always generic ({"status":"ok"}) — never reveals a match,
      // so the code pane advances regardless of match outcome. It only
      // advances on a successful call though — a network/server failure
      // must not claim we've emailed a code.
      setRequestSent(true)
      setStage('code')
    } catch (err) {
      setRequestError(err.response?.data?.detail || 'Something went wrong. Try again.')
    } finally {
      setRequestBusy(false)
    }
  }

  const handleRedeem = async () => {
    setRedeemBusy(true)
    setRedeemError(null)
    try {
      await billingApi.acceptTerms()
      await promoApi.redeem(code.trim())
      navigate('/dashboard')
    } catch (err) {
      setRedeemError(err.response?.data?.detail || 'Something went wrong. Try again.')
      setRedeemBusy(false)
    }
  }

  const handlePay = async () => {
    setPayBusy(true)
    setPayError(null)
    try {
      await billingApi.acceptTerms()
      const { checkout_url } = await billingApi.checkout(plan)
      window.location.assign(checkout_url)
    } catch (err) {
      setPayError(err.response?.data?.detail || 'Something went wrong. Try again.')
      setPayBusy(false)
    }
  }

  return (
    <Screen>
      <header className="animate-fade-in text-center">
        <BrandHeading className="text-4xl">Before we start</BrandHeading>
      </header>

      <div className="mt-6 animate-slide-in rounded-2xl bg-white/70 p-5 text-sm leading-relaxed text-slate-700 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:text-slate-200 dark:ring-slate-700">
        <p>Hey there! Welcome to Lease Check.</p>
        <p className="mt-3">
          We’re excited to help you navigate your lease agreements with ease. Before you
          get started, we want to make sure you’re aware of a few important things:
        </p>
        <ul className="mt-3 list-disc space-y-2 pl-5">
          <li>
            <span className="font-semibold text-emerald-700 dark:text-emerald-400">Terms of Service:</span>{' '}
            the rules and guidelines for using our app — from how you can use Lease
            Check to what you can expect from us.
          </li>
          <li>
            <span className="font-semibold text-emerald-700 dark:text-emerald-400">Disclaimer:</span>{' '}
            the limitations of our service. While we strive to provide accurate and
            helpful information, it’s not a substitute for professional legal advice.
          </li>
          <li>
            <span className="font-semibold text-emerald-700 dark:text-emerald-400">Refund Policy:</span>{' '}
            our policies regarding refunds. We want to ensure you're satisfied with your
            experience.
          </li>
        </ul>
        <p className="mt-3">
          Got questions? Reach out at{' '}
          <a className="text-emerald-700 underline dark:text-emerald-400" href="mailto:support@coloradoleasecheck.com">
            support@coloradoleasecheck.com
          </a>
          . By using Lease Check, you agree to our Terms of Service, Disclaimer, and
          Refund Policy.
        </p>
      </div>

      <div className="mt-6 flex animate-slide-in flex-col items-center gap-2 text-base font-semibold">
        <Link className="text-emerald-700 underline dark:text-emerald-400" to="/terms">Terms Of Service</Link>
        <Link className="text-emerald-700 underline dark:text-emerald-400" to="/disclaimer">Legal Disclaimer</Link>
        <Link className="text-emerald-700 underline dark:text-emerald-400" to="/refund-policy">Refund Policy</Link>
      </div>

      <div className="mt-8 animate-slide-in rounded-2xl bg-white/70 p-5 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700">
        {stage === 'request' ? (
          <>
            <label htmlFor="promo-email" className={labelClass}>Email</label>
            <input
              id="promo-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={requestBusy}
              className={inputClass}
            />
            <label htmlFor="promo-property-name" className={`mt-3 ${labelClass}`}>Property name</label>
            <input
              id="promo-property-name"
              type="text"
              value={propertyName}
              onChange={(e) => setPropertyName(e.target.value)}
              disabled={requestBusy}
              className={inputClass}
            />
            <label htmlFor="promo-property-address" className={`mt-3 ${labelClass}`}>Property address</label>
            <input
              id="promo-property-address"
              type="text"
              value={propertyAddress}
              onChange={(e) => setPropertyAddress(e.target.value)}
              disabled={requestBusy}
              className={inputClass}
            />
            {requestError && (
              <p className="mt-2 text-sm text-red-700 dark:text-red-400">{requestError}</p>
            )}
            <PrimaryButton
              onClick={handleRequest}
              disabled={requestBusy || !email.trim() || !propertyName.trim() || !propertyAddress.trim()}
              className="mt-3"
            >
              {requestBusy ? 'Sending…' : 'Request code'}
            </PrimaryButton>
            <button
              onClick={() => setStage('code')}
              className="mt-3 w-full text-center text-sm font-semibold text-emerald-700 underline dark:text-emerald-400"
            >
              Already have a code?
            </button>
          </>
        ) : (
          <>
            {requestSent && (
              <p className="mb-3 text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                If this is a participating property, we've emailed a one-time code.
              </p>
            )}
            <label htmlFor="promo-code" className={labelClass}>Code</label>
            <input
              id="promo-code"
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              disabled={redeemBusy}
              className={inputClass}
            />
            {redeemError && (
              <p className="mt-2 text-sm text-red-700 dark:text-red-400">{redeemError}</p>
            )}
            <PrimaryButton onClick={handleRedeem} disabled={redeemBusy || !code.trim()} className="mt-3">
              {redeemBusy ? 'One moment…' : 'Redeem & continue'}
            </PrimaryButton>
          </>
        )}
      </div>

      {PAYMENTS_ENABLED && (
        <>
          {payError && <p className="mt-4 text-center text-sm text-red-700 dark:text-red-400">{payError}</p>}
          <div className="mt-8 flex animate-slide-in flex-col gap-3">
            <PrimaryButton onClick={handlePay} disabled={payBusy}>
              {payBusy ? 'One moment…' : 'Continue to payment'}
            </PrimaryButton>
            <button
              onClick={() => navigate('/terms-declined')}
              disabled={payBusy}
              className="w-full rounded-xl bg-white py-4 text-lg font-bold text-emerald-700 ring-1 ring-emerald-200 transition hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-800/60 dark:text-emerald-400 dark:ring-slate-700"
            >
              Reject
            </button>
          </div>
        </>
      )}
    </Screen>
  )
}

export default LegalGate
